import { useState } from 'react';
import { Box, SimpleGrid, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { CardComponent } from '@themes/aegix';
import {
  HiShieldCheck,
  HiClipboardDocumentList,
  HiDocumentText,
  HiPrinter,
  HiChartBar,
  HiBookOpen,
} from 'react-icons/hi2';
import { IoChevronForwardCircleOutline } from 'react-icons/io5';
import { useHasPermission } from '@aegix/auth-permissions';
import { ReadinessOverview } from '../documents/ReadinessOverview';
import { ComplianceReports } from '../documents/ComplianceReports';
import { IncidentReports } from '../documents/IncidentReports';
import { ProceduresLibrary } from '../documents/ProceduresLibrary';
import { LabelsSignage } from '../documents/LabelsSignage';

type DocumentView =
  | 'menu'
  | 'readiness'
  | 'compliance'
  | 'incidents'
  | 'procedures'
  | 'labels';

export function DocumentsContent() {
  const [view, setView] = useState<DocumentView>('menu');
  const navigate = useNavigate();
  const canViewAdminGuides = useHasPermission('accounts.view_admin_guides');

  const backToMenu = () => setView('menu');

  if (view === 'readiness') return <ReadinessOverview onBack={backToMenu} />;
  if (view === 'compliance') return <ComplianceReports onBack={backToMenu} />;
  if (view === 'incidents') return <IncidentReports onBack={backToMenu} />;
  if (view === 'procedures') return <ProceduresLibrary onBack={backToMenu} />;
  if (view === 'labels') return <LabelsSignage onBack={backToMenu} />;

  return (
    <Box px={{ base: 4, md: 8 }} py={6}>
      <Text fontSize="lg" fontWeight={600} color="text.primary" mb={2}>
        Organization Documents
      </Text>
      <Text fontSize="sm" color="text.secondary" mb={6}>
        Safety plans, compliance reports, and readiness assessments for your
        organization.
      </Text>

      <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} gap={4}>
        <CardComponent
          title="Readiness Assessments"
          description="District and site readiness scores"
          leftIcon={<HiChartBar size={30} />}
          rightIcon={<IoChevronForwardCircleOutline size={30} />}
          onButtonClick={() => setView('readiness')}
        />

        <CardComponent
          title="Compliance Reports"
          description="ILUG SRM compliance audits"
          leftIcon={<HiShieldCheck size={30} />}
          rightIcon={<IoChevronForwardCircleOutline size={30} />}
          onButtonClick={() => setView('compliance')}
        />

        <CardComponent
          title="Incident Reports"
          description="After-action reports and incident data exports"
          leftIcon={<HiClipboardDocumentList size={30} />}
          rightIcon={<IoChevronForwardCircleOutline size={30} />}
          onButtonClick={() => setView('incidents')}
        />

        <CardComponent
          title="Emergency Procedures"
          description="Guidance checklists by alert type"
          leftIcon={<HiDocumentText size={30} />}
          rightIcon={<IoChevronForwardCircleOutline size={30} />}
          onButtonClick={() => setView('procedures')}
        />

        <CardComponent
          title="Labels & Signage"
          description="Reunification signage templates"
          leftIcon={<HiPrinter size={30} />}
          rightIcon={<IoChevronForwardCircleOutline size={30} />}
          onButtonClick={() => setView('labels')}
        />

        {canViewAdminGuides && (
          <CardComponent
            title="Admin Setup Guides"
            description="In-app SAML, SCIM, and OAuth setup runbooks"
            leftIcon={<HiBookOpen size={30} />}
            rightIcon={<IoChevronForwardCircleOutline size={30} />}
            onButtonClick={() => navigate('/account/docs')}
          />
        )}
      </SimpleGrid>
    </Box>
  );
}
